import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { SectionTitle } from 'src/common/components/SectionTitle';
import { CourseCard } from 'src/common/components/CourseCard';
import { SwiperSlide } from 'swiper/react';
import { media } from 'src/config/breakpoints';
import { Button } from 'src/common/components/Button';
import { EmptyCourseCard } from 'src/common/components/EmptyCourseCard';
import { CourseCardSwiper, NavigationButton } from 'src/common/components/CourseCardSwiper';
import { rem } from 'polished';
import { SectionSubtitle } from 'src/common/components/SectionSubtitle';
import { CourseExcerpt } from 'src/common/models/courseExcerpt';
import { isEmpty } from 'ramda';
import { GatsbyLink } from 'src/common/migration/GatsbyLink';
import { FreeMode, SwiperOptions } from 'swiper';
import { SearchRoute } from 'src/search/routes';

const ButtonContainer = styled.div`
  ${Button} {
    margin-left: 1.875rem;
  }
`;

const Card = styled.div<{ featured?: boolean }>`
  width: ${({ featured }) => (featured ? rem(245) : rem(212))};

  ${media.w.min.px992(css`
    flex: 1 1 auto;
    padding: 0 0.75rem;
    width: auto;
  `)};
`;

const Container = styled.div`
  margin-top: 3.75rem;
  ${media.w.min.px768(css`
    margin-top: 3.25rem;
  `)}

  ${media.w.min.px1200(css`
    margin-top: 4.5rem;
  `)}
`;

const CourseCards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  margin: 0 -0.75rem;
  padding: 1.625rem 0 1.875rem;
`;

const Desktop = styled.div`
  margin-top: 1.25rem;
  ${media.w.max.px992(css`
    display: none;
  `)};
`;

const Mobile = styled.div`
  margin-top: 1.25rem;
  ${media.w.min.px992(css`
    display: none;
  `)};
`;

const StyledSectionTitle = styled(SectionTitle)`
  margin: 0;
  ${media.w.min.px768(css`
    margin-left: 1.25rem;
  `)}
`;

const StyledSectionSubtitle = styled(SectionSubtitle)`
  margin: 1rem 0 0 0;
  ${media.w.min.px768(css`
    margin-left: 1.25rem;
  `)};
`;

const StyledCourseCardSwiper = styled(CourseCardSwiper)`
  ${NavigationButton} {
    display: none;
  }

  .swiper-scrollbar {
    ${media.w.min.px992(css`
      display: none;
    `)};
  }

  .swiper-slide {
    width: auto;
  }
`;

const swiperOptions: SwiperOptions = {
  centeredSlides: true,
  centeredSlidesBounds: true,
  freeMode: {
    enabled: true,
  },
  modules: [FreeMode],
  slidesPerView: 'auto',
};

export interface SpeakerPageLatestCoursesProps {
  speakerId: number;
  courseExcerpts: CourseExcerpt[];
}

export const SpeakerPageLatestCourses: FC<SpeakerPageLatestCoursesProps> = ({
  speakerId,
  courseExcerpts: [firstCourseExcerpt, ...restCourseExcerpts],
}) => (
  <Container>
    <StyledSectionTitle>Szkolenia</StyledSectionTitle>
    <StyledSectionSubtitle>Warto dołączyć</StyledSectionSubtitle>
    <Desktop>
      {firstCourseExcerpt && <CourseCard featured courseExcerpt={firstCourseExcerpt} />}
      {!isEmpty(restCourseExcerpts) && (
        <>
          <CourseCards>
            {restCourseExcerpts.map((courseExcerpt) => (
              <Card key={courseExcerpt.courseId}>
                <CourseCard courseExcerpt={courseExcerpt} />
              </Card>
            ))}
          </CourseCards>

          <ButtonContainer>
            <Button as={GatsbyLink} kind="border" to={SearchRoute} state={{ speakerId }}>
              Zobacz więcej
            </Button>
          </ButtonContainer>
        </>
      )}
    </Desktop>

    <Mobile>
      {isEmpty(restCourseExcerpts) && firstCourseExcerpt ? (
        <CourseCard featured featuredBreakpoint="px768" courseExcerpt={firstCourseExcerpt} />
      ) : (
        <StyledCourseCardSwiper options={swiperOptions}>
          {firstCourseExcerpt && (
            <SwiperSlide key={firstCourseExcerpt.courseId}>
              <Card featured>
                <CourseCard
                  featured
                  featuredBreakpoint="px992"
                  courseExcerpt={firstCourseExcerpt}
                />
              </Card>
            </SwiperSlide>
          )}

          {restCourseExcerpts.map((courseExcerpt) => (
            <SwiperSlide key={courseExcerpt.courseId}>
              <Card>
                <CourseCard featured featuredBreakpoint="px992" courseExcerpt={courseExcerpt} />
              </Card>
            </SwiperSlide>
          ))}

          <SwiperSlide>
            <EmptyCourseCard url={SearchRoute} />
          </SwiperSlide>
        </StyledCourseCardSwiper>
      )}
    </Mobile>
  </Container>
);
